/**
 * Danh mục quyền (RBAC). Mã quyền là hợp đồng ổn định giữa backend và frontend.
 * Backend luôn kiểm tra quyền; frontend chỉ dùng để ẩn/hiện điều hướng.
 */
export const PERMISSIONS = {
  // Tài khoản & phân quyền
  USER_MANAGE: 'user.manage',
  ROLE_MANAGE: 'role.manage',
  // Vật tư
  PRODUCT_VIEW: 'product.view',
  PRODUCT_MANAGE: 'product.manage',
  PRODUCT_VIEW_COST: 'product.view_cost',
  PRODUCT_SET_CODE: 'product.set_code',
  // Hãng & tương thích
  MANUFACTURER_MANAGE: 'manufacturer.manage',
  COMPATIBILITY_MANAGE: 'compatibility.manage',
  // Gói sản phẩm
  PACKAGE_VIEW: 'package.view',
  PACKAGE_MANAGE: 'package.manage',
  PACKAGE_PUBLISH: 'package.publish',
  PACKAGE_VIEW_COST: 'package.view_cost',
  PACKAGE_VIEW_ALL_CUSTOM: 'package.view_all_custom',
  // Tài chính
  FINANCING_VIEW: 'financing.view',
  FINANCING_MANAGE: 'financing.manage',
  PAYBACK_YIELD_MANAGE: 'payback.yield_manage',
  // Báo giá
  QUOTE_CREATE: 'quote.create',
  QUOTE_VIEW_ALL: 'quote.view_all',
  QUOTE_MANAGE_ALL: 'quote.manage_all',
  QUOTE_VIEW_TEAM: 'quote.view_team',
  QUOTE_MANAGE_TEAM: 'quote.manage_team',
  QUOTE_STATS: 'quote.stats',
  QUOTE_OVERRIDE_SALESPERSON: 'quote.override_salesperson',
  QUOTE_DISCOUNT_UNLIMITED: 'quote.discount_unlimited',
  QUOTE_EXTRA_COSTS: 'quote.extra_costs',
  QUOTE_DELETE: 'quote.delete',
  // Mẫu báo giá
  TEMPLATE_MANAGE: 'template.manage',
  // Bản tin
  POST_VIEW: 'post.view',
  POST_INTERACT: 'post.interact',
  POST_MANAGE: 'post.manage',
  // Hệ thống
  SETTINGS_MANAGE: 'settings.manage',
  AUDIT_VIEW: 'audit.view',
  // Nhân sự kỹ thuật & kinh doanh (nguồn dữ liệu chung)
  STAFF_VIEW: 'staff.view',
  STAFF_MANAGE: 'staff.manage',
  // Điểm số nhân sự
  SCORE_VIEW: 'score.view',
  SCORE_MANAGE: 'score.manage',
  // Tích hợp hệ thống ngoài (khóa API)
  INTEGRATION_MANAGE: 'integration.manage',
  // Đặt lịch khảo sát kỹ thuật
  BOOKING_USE: 'booking.use',
  BOOKING_MANAGE: 'booking.manage',
  // Các module ứng dụng
  DESIGN_USE: 'design.use',
  DESIGN_STATS: 'design.stats',
  DESIGN_VIEW_ALL: 'design.view_all',
  DESIGN_DELETE: 'design.delete',
  SIMULATOR_USE: 'simulator.use',
  FEASIBILITY_REPORT: 'report.feasibility',
  CONSTRUCTION_ACCESS: 'construction.access',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const PERMISSION_DEFS: { code: PermissionCode; name: string; group: string }[] = [
  { code: 'user.manage', name: 'Quản lý tài khoản nhân viên', group: 'Tài khoản' },
  { code: 'role.manage', name: 'Quản lý vai trò & phân quyền', group: 'Tài khoản' },
  { code: 'product.view', name: 'Xem danh mục vật tư', group: 'Vật tư' },
  { code: 'product.manage', name: 'Thêm/sửa/ngừng kinh doanh vật tư', group: 'Vật tư' },
  { code: 'product.view_cost', name: 'Xem giá vốn vật tư', group: 'Vật tư' },
  { code: 'product.set_code', name: 'Tự nhập mã vật tư', group: 'Vật tư' },
  { code: 'manufacturer.manage', name: 'Quản lý hãng sản xuất', group: 'Hãng & tương thích' },
  {
    code: 'compatibility.manage',
    name: 'Cấu hình tương thích biến tần – pin lưu trữ',
    group: 'Hãng & tương thích',
  },
  {
    code: 'package.view',
    name: 'Xem mọi gói sản phẩm (kể cả bản nháp, lịch sử phiên bản)',
    group: 'Gói sản phẩm',
  },
  { code: 'package.manage', name: 'Tạo/sửa bản nháp gói sản phẩm', group: 'Gói sản phẩm' },
  { code: 'package.publish', name: 'Phát hành / ngừng kinh doanh gói', group: 'Gói sản phẩm' },
  { code: 'package.view_cost', name: 'Xem giá vốn dự kiến của gói', group: 'Gói sản phẩm' },
  {
    code: 'package.view_all_custom',
    name: 'Xem tất cả gói tùy chỉnh (mặc định chỉ người tạo, quản lý đội của người tạo và người được chia sẻ)',
    group: 'Gói sản phẩm',
  },
  { code: 'financing.view', name: 'Xem chương trình vay & công cụ tính', group: 'Tài chính' },
  { code: 'financing.manage', name: 'Quản lý chương trình vay', group: 'Tài chính' },
  {
    code: 'payback.yield_manage',
    name: 'Cài đặt hệ số sản lượng mặc định theo tỉnh/thành (ước tính hoàn vốn)',
    group: 'Tài chính',
  },
  { code: 'quote.create', name: 'Tạo & sửa báo giá của mình', group: 'Báo giá' },
  { code: 'quote.view_all', name: 'Xem báo giá của tất cả nhân viên', group: 'Báo giá' },
  { code: 'quote.manage_all', name: 'Sửa/chuyển trạng thái báo giá của người khác', group: 'Báo giá' },
  { code: 'quote.view_team', name: 'Xem báo giá của các đội mình quản lý', group: 'Báo giá' },
  { code: 'quote.manage_team', name: 'Sửa/chuyển trạng thái báo giá của đội mình quản lý', group: 'Báo giá' },
  { code: 'quote.stats', name: 'Xem thống kê báo giá (theo phạm vi được xem)', group: 'Báo giá' },
  { code: 'quote.override_salesperson', name: 'Sửa tên/thông tin người báo giá', group: 'Báo giá' },
  { code: 'quote.discount_unlimited', name: 'Chiết khấu vượt giới hạn cấu hình', group: 'Báo giá' },
  { code: 'quote.extra_costs', name: 'Thêm chi phí bổ sung vào báo giá', group: 'Báo giá' },
  { code: 'quote.delete', name: 'Xóa vĩnh viễn báo giá (kể cả đã phát hành & PDF)', group: 'Báo giá' },
  { code: 'template.manage', name: 'Quản lý mẫu báo giá HTML', group: 'Mẫu báo giá' },
  { code: 'post.view', name: 'Xem tin nội bộ', group: 'Bản tin' },
  { code: 'post.interact', name: 'Thích & bình luận tin nội bộ', group: 'Bản tin' },
  { code: 'post.manage', name: 'Đăng/sửa/ghim/ẩn tin nội bộ', group: 'Bản tin' },
  { code: 'settings.manage', name: 'Cài đặt hệ thống & thông tin doanh nghiệp', group: 'Hệ thống' },
  { code: 'audit.view', name: 'Xem nhật ký hoạt động', group: 'Hệ thống' },
  { code: 'staff.view', name: 'Xem danh sách nhân sự kỹ thuật & kinh doanh', group: 'Nhân sự' },
  { code: 'staff.manage', name: 'Thêm/sửa/ngừng hoạt động nhân sự', group: 'Nhân sự' },
  { code: 'score.view', name: 'Xem điểm số & lịch sử điểm nhân sự', group: 'Nhân sự' },
  { code: 'score.manage', name: 'Cập nhật điểm số nhân sự', group: 'Nhân sự' },
  { code: 'integration.manage', name: 'Quản lý khóa API cho hệ thống ngoài', group: 'Hệ thống' },
  { code: 'booking.use', name: 'Đặt lịch khảo sát & xem lịch kỹ thuật', group: 'Lịch kỹ thuật' },
  { code: 'booking.manage', name: 'Cập nhật/hủy lịch kỹ thuật thủ công', group: 'Lịch kỹ thuật' },
  { code: 'design.use', name: 'Dùng công cụ thiết kế & bố trí tấm pin', group: 'Ứng dụng' },
  { code: 'design.stats', name: 'Xem thống kê thiết kế theo nhân sự (toàn công ty)', group: 'Ứng dụng' },
  {
    code: 'design.view_all',
    name: 'Xem & mở lại thiết kế và mô phỏng của tất cả nhân sự (lưu trên hệ thống)',
    group: 'Ứng dụng',
  },
  { code: 'simulator.use', name: 'Dùng công cụ mô phỏng điện mặt trời', group: 'Ứng dụng' },
  {
    code: 'design.delete',
    name: 'Xóa thiết kế & mô phỏng đã lưu của mọi nhân sự (kể cả thiết kế đã phát hành)',
    group: 'Ứng dụng',
  },
  {
    code: 'report.feasibility',
    name: 'Lập báo cáo đánh giá kinh tế kỹ thuật & hiệu quả đầu tư (xuất Word)',
    group: 'Ứng dụng',
  },
  { code: 'construction.access', name: 'Mở module quản lý thi công', group: 'Ứng dụng' },
];

const ALL = PERMISSION_DEFS.map((p) => p.code);

/** Vai trò mặc định được seed. Admin có thể điều chỉnh quyền trong giao diện. */
export const DEFAULT_ROLES: {
  code: string;
  name: string;
  description: string;
  permissions: PermissionCode[];
}[] = [
  { code: 'ADMIN', name: 'Quản trị viên', description: 'Toàn quyền hệ thống', permissions: ALL },
  // Vai trò cũ MANAGER ("Quản lý") đã bỏ — quyết định P-6 2026-10-21: là Quản lý nhóm kinh doanh (SALES_MANAGER).
  // Người đang giữ được chuyển sang SALES_MANAGER khi seed (bootstrap.ts → retireLegacyManagerRole).
  {
    code: 'SALES',
    name: 'Nhân viên kinh doanh',
    description: 'Tư vấn khách hàng, tạo báo giá của mình, đặt lịch khảo sát, xem dự án thi công được phân',
    // Nhân viên chỉ thấy gói ĐANG HOẠT ĐỘNG (qua quyền quote.create), không thấy bản nháp
    permissions: [
      'product.view',
      'financing.view',
      'quote.create',
      'quote.stats',
      'post.view',
      'post.interact',
      'booking.use',
      'design.use',
      'simulator.use',
      // Vào module thi công; quyền & phạm vi bên trong do vai trò thi công của tài khoản liên kết quyết định
      'construction.access',
    ],
  },
  // ---- Các vai trò nghiệp vụ bổ sung (QA 2026-10 — docs/QA/PERMISSION_MATRIX.md) ----
  {
    code: 'TECHNICAL',
    name: 'Nhân viên kỹ thuật',
    description: 'Khảo sát, thiết kế & mô phỏng, theo dõi thi công dự án được phân; không xem/sửa thông tin thương mại',
    permissions: [
      'product.view',
      'post.view',
      'post.interact',
      'booking.use',
      'design.use',
      'simulator.use',
      'report.feasibility',
      'construction.access',
    ],
  },
  {
    code: 'MATERIALS',
    name: 'Nhân viên vật tư',
    description: 'Quản lý danh mục vật tư, hãng sản xuất, tương thích thiết bị; theo dõi tình trạng vật tư dự án',
    permissions: [
      'product.view',
      'product.manage',
      'product.view_cost',
      'product.set_code',
      'manufacturer.manage',
      'compatibility.manage',
      'package.view',
      'post.view',
      'post.interact',
      'construction.access',
    ],
  },
  {
    code: 'SALES_MANAGER',
    name: 'Quản lý nhóm kinh doanh',
    description: 'Quản lý báo giá & tiến độ của các đội mình phụ trách (không xem toàn công ty), phân lịch khảo sát',
    permissions: [
      'product.view',
      'package.view',
      'financing.view',
      'quote.create',
      'quote.view_team',
      'quote.manage_team',
      'quote.stats',
      'quote.override_salesperson',
      'quote.extra_costs',
      'post.view',
      'post.interact',
      'staff.view',
      'score.view',
      'booking.use',
      'booking.manage',
      'design.use',
      'simulator.use',
      'construction.access',
    ],
  },
  {
    code: 'SALE_ADMIN',
    name: 'Sale admin',
    description: 'Hỗ trợ vận hành kinh doanh: xem toàn bộ báo giá (không xem giá vốn), mẫu báo giá, lịch khảo sát, tin nội bộ',
    permissions: [
      'product.view',
      'package.view',
      'financing.view',
      'quote.create',
      'quote.view_all',
      'quote.stats',
      'quote.override_salesperson',
      'template.manage',
      'post.view',
      'post.interact',
      'post.manage',
      'staff.view',
      'booking.use',
      'booking.manage',
      'design.use',
      'simulator.use',
      'construction.access',
    ],
  },
  {
    code: 'CUSTOMER_SERVICE',
    name: 'Chăm sóc khách hàng',
    description: 'Theo dõi lịch khảo sát và tiến độ thi công để trả lời khách; không xem giá, không sửa thiết kế/cấu hình',
    permissions: ['post.view', 'post.interact', 'booking.use', 'construction.access'],
  },
];

/** Phạm vi (scope) của khóa API cho hệ thống ngoài. */
export const API_SCOPES = ['staff:read', 'staff:write', 'score:read', 'score:write', 'package:read'] as const;
export type ApiScope = (typeof API_SCOPES)[number];
